import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon as I } from '../components/Icons.jsx';
import { api } from '../services/api/client.js';

/* ============================================================
   Messages — Instagram Direct Messages inbox.
   Exercises `instagram_business_manage_messages`. Two-pane layout:
     - left: conversations list (sender username + last update)
     - right: message thread + composer
   Polls list every 15s and active thread every 10s while focused.
   Send is constrained by Meta's 24h messaging window — outside that
   window Meta returns error code 10 and we surface the message inline.
   ============================================================ */

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function Messages() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [convosLoading, setConvosLoading] = useState(true);
  const [convosError, setConvosError] = useState(null);
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [activeConvo, setActiveConvo] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [convosRefreshKey, setConvosRefreshKey] = useState(0);
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);

  // Bootstrap — load connected IG accounts so the user can pick which inbox.
  // Honors ?accountId=... query param so deep-links from Settings → Connected
  // accounts → "Messages" land directly on the right inbox.
  useEffect(() => {
    let cancelled = false;
    api.socialAccounts.list().then((res) => {
      if (cancelled) return;
      const list = (res?.accounts || res?.data?.accounts || [])
        .filter((a) => a.platform === 'instagram' && a.status === 'connected');
      setAccounts(list);
      if (list.length > 0 && !selectedAccountId) {
        const qp = new URLSearchParams(window.location.search);
        const requested = qp.get('accountId');
        const match = requested ? list.find((a) => a.id === requested) : null;
        setSelectedAccountId((match || list[0]).id);
      }
    }).catch(() => { /* ignore — empty state handled below */ });
    return () => { cancelled = true; };
  }, []);  // run once

  // Load conversations for the selected account.
  useEffect(() => {
    if (!selectedAccountId) return;
    let cancelled = false;
    setConvosLoading(true);
    setConvosError(null);
    api.instagram.listConversations(selectedAccountId)
      .then((res) => { if (!cancelled) setConversations(res?.conversations || []); })
      .catch((err) => { if (!cancelled) setConvosError(err.message || t('messages.couldNotLoadConversations')); })
      .finally(() => { if (!cancelled) setConvosLoading(false); });
    return () => { cancelled = true; };
  }, [selectedAccountId, convosRefreshKey]);

  // Auto-refresh conversations every 15s while tab focused.
  useEffect(() => {
    if (!selectedAccountId) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setConvosRefreshKey((k) => k + 1);
      }
    }, 15000);
    return () => clearInterval(id);
  }, [selectedAccountId]);

  // Load active conversation thread.
  useEffect(() => {
    if (!activeConvoId || !selectedAccountId) {
      setActiveConvo(null);
      return;
    }
    let cancelled = false;
    setThreadLoading(true);
    setThreadError(null);
    api.instagram.getConversation(activeConvoId, selectedAccountId)
      .then((res) => { if (!cancelled) setActiveConvo(res || null); })
      .catch((err) => { if (!cancelled) setThreadError(err.message || t('messages.couldNotLoadThread')); })
      .finally(() => { if (!cancelled) setThreadLoading(false); });
    return () => { cancelled = true; };
  }, [activeConvoId, selectedAccountId, threadRefreshKey]);

  // Auto-refresh active thread every 10s while focused.
  useEffect(() => {
    if (!activeConvoId) return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setThreadRefreshKey((k) => k + 1);
      }
    }, 10000);
    return () => clearInterval(id);
  }, [activeConvoId]);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.id === selectedAccountId) || null,
    [accounts, selectedAccountId]
  );

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || !activeConvoId || sending) return;
    setSending(true);
    setThreadError(null);
    try {
      await api.instagram.sendMessage(activeConvoId, text, selectedAccountId);
      setDraft('');
      // Pull fresh thread so the new message appears in the chronological list.
      setThreadRefreshKey((k) => k + 1);
      setConvosRefreshKey((k) => k + 1);
    } catch (err) {
      setThreadError(err?.message || t('messages.couldNotSendMessage'));
    } finally {
      setSending(false);
    }
  };

  // ----- Empty states -----
  if (accounts.length === 0) {
    return (
      <div className="screen-pad" style={{ maxWidth: 720 }}>
        <h1 className="h1">{t('messages.title')}</h1>
        <div className="muted" style={{ marginTop: 16 }}>
          {t('messages.emptyConnectPrefix')}{' '}
          <a href="/app/settings?tab=social" className="link">{t('messages.settingsConnectedAccountsLink')}</a>
          {' '}{t('messages.emptyConnectSuffix')}
        </div>
      </div>
    );
  }

  return (
    <div className="screen-pad" style={{ maxWidth: 1180 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, flexWrap: 'wrap', gap: 12 }}>
        <h1 className="h1" style={{ margin: 0 }}>{t('messages.title')}</h1>
        {accounts.length > 1 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{t('messages.inboxLabel')}</span>
            <select
              value={selectedAccountId || ''}
              onChange={(e) => { setSelectedAccountId(e.target.value); setActiveConvoId(null); }}
              className="select"
              style={{ fontSize: 13, padding: '4px 8px' }}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName || a.handle || a.externalAccountId}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '320px 1fr',
          gap: 14,
          height: 'min(calc(100vh - 220px), 720px)',
          minHeight: 480,
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-lg)',
          overflow: 'hidden',
          background: 'var(--bg)',
        }}
      >
        {/* Left: conversations sidebar */}
        <aside
          style={{
            borderRight: '1px solid var(--border)',
            background: 'var(--bg-elev)',
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <div style={{
            padding: '10px 12px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}>
            <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
              {selectedAccount ? `@${(selectedAccount.handle || selectedAccount.displayName || '').replace(/^@/, '')}` : t('messages.inbox')}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setConvosRefreshKey((k) => k + 1)}
              disabled={convosLoading}
              title={t('messages.refresh')}
            >
              <I.refresh size={11} />
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {convosLoading && conversations.length === 0 && (
              <div className="muted" style={{ padding: 14, fontSize: 13 }}>{t('messages.loading')}</div>
            )}
            {convosError && (
              <div className="auth-error" style={{ margin: 10 }}>
                <I.alert size={13} /> <span>{convosError}</span>
              </div>
            )}
            {!convosLoading && conversations.length === 0 && !convosError && (
              <div className="muted" style={{ padding: 14, fontSize: 13 }}>
                {t('messages.noConversationsYet')}
              </div>
            )}
            {conversations.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {conversations.map((c) => {
                  const active = c.id === activeConvoId;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setActiveConvoId(c.id)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          background: active ? 'var(--bg-subtle)' : 'transparent',
                          border: 0,
                          borderBottom: '1px solid var(--border-subtle)',
                          padding: '10px 12px',
                          cursor: 'pointer',
                          color: 'inherit',
                          fontFamily: 'inherit',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                          <span style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            @{c.participant?.username || t('messages.userFallback')}
                          </span>
                          <span className="mono" style={{ fontSize: 10, color: 'var(--text-subtle)', flexShrink: 0 }}>
                            {fmtDate(c.lastMessage?.createdTime || c.updatedTime)}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Right: thread + composer */}
        <section style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!activeConvoId && (
            <div className="muted" style={{ padding: 28, fontSize: 13, textAlign: 'center', margin: 'auto' }}>
              {t('messages.selectConversation')}
            </div>
          )}

          {activeConvoId && (
            <>
              <header style={{
                padding: '10px 14px',
                borderBottom: '1px solid var(--border-subtle)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    @{activeConvo?.participant?.username || t('messages.userFallback')}
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                    Instagram Direct
                  </div>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  onClick={() => setThreadRefreshKey((k) => k + 1)}
                  disabled={threadLoading}
                >
                  <I.refresh size={11} /> {t('messages.refresh')}
                </button>
              </header>

              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {threadLoading && !activeConvo && (
                  <div className="muted" style={{ fontSize: 13 }}>{t('messages.loadingMessages')}</div>
                )}
                {threadError && (
                  <div className="auth-error" style={{ margin: '4px 0' }}>
                    <I.alert size={13} /> <span>{threadError}</span>
                  </div>
                )}
                {activeConvo?.messages?.length === 0 && !threadLoading && (
                  <div className="muted" style={{ fontSize: 13 }}>{t('messages.noMessagesYet')}</div>
                )}
                {activeConvo?.messages?.map((m) => (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: m.from?.isOwner ? 'flex-end' : 'flex-start',
                      maxWidth: '78%',
                      padding: '8px 12px',
                      background: m.from?.isOwner ? 'var(--accent)' : 'var(--bg-elev)',
                      color: m.from?.isOwner ? 'var(--accent-fg, #fff)' : 'var(--text)',
                      borderRadius: 'var(--r-md)',
                      border: m.from?.isOwner ? 'none' : '1px solid var(--border-subtle)',
                    }}
                  >
                    <div style={{ fontSize: 13.5, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.text || <em style={{ opacity: 0.6 }}>{t('messages.noText')}</em>}
                    </div>
                    <div className="mono" style={{ fontSize: 10, opacity: 0.7, marginTop: 4, textAlign: m.from?.isOwner ? 'right' : 'left' }}>
                      {fmtDate(m.createdTime)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Composer */}
              <div style={{
                borderTop: '1px solid var(--border-subtle)',
                padding: 10,
                background: 'var(--bg-elev)',
              }}>
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={t('messages.writeAMessage')}
                  rows={2}
                  disabled={sending}
                  style={{
                    width: '100%',
                    border: '1px solid var(--border)',
                    outline: 0,
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 14,
                    lineHeight: 1.5,
                    resize: 'vertical',
                    minHeight: 44,
                    padding: 8,
                    borderRadius: 'var(--r-md)',
                  }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 8,
                  gap: 8,
                }}>
                  <span className="mono" style={{ fontSize: 11, color: 'var(--text-subtle)' }}>
                    {t('messages.replyWindowNote')}
                  </span>
                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    onClick={handleSend}
                    disabled={!draft.trim() || sending}
                    style={{ minWidth: 84, justifyContent: 'center' }}
                  >
                    {sending ? (
                      <>
                        <span className="dot pulse" style={{ background: 'currentColor', width: 6, height: 6 }} />
                        {t('messages.sending')}
                      </>
                    ) : (
                      <><I.send size={12} /> {t('messages.send')}</>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default Messages;
